import { BellRing, Mic, WifiOff, AudioWaveform } from 'lucide-react';
import { Reveal } from '@/components/Reveal';
import { MiniEq } from '@/components/MiniEq';
import { HOME } from '@/constants/testIds';
import { scrollToId } from '@/lib/scrollTo';

const MIC_DETAIL =
    'https://static.prod-images.emergentagent.com/jobs/026e8d45-2a45-421d-be3c-026a565f857e/images/361a9449c5b9edc954a3a446b89209e67dd4fa56412fb6ec19108ecc4bec32f0.jpeg';

const CardShell = ({ className = '', children }) => (
    <article
        className={`group relative overflow-hidden rounded-2xl border border-white/10 bg-surface-2 transition-all duration-300 hover:-translate-y-1 hover:border-white/25 hover:shadow-glow-soft ${className}`}
    >
        <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
            style={{ background: 'radial-gradient(80% 60% at 50% 0%, rgba(0,229,255,0.07), transparent)' }}
        />
        {children}
    </article>
);

const IconChip = ({ icon: Icon }) => (
    <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-aqua/30 bg-aqua/10 text-aqua">
        <Icon className="h-4 w-4" />
    </div>
);

export const Features = () => (
    <section id="features" data-testid={HOME.features} className="relative py-24 md:py-32">
        <div className="mx-auto max-w-6xl px-6">
            <Reveal>
                <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
                    <div>
                        <p className="font-mono text-xs uppercase tracking-[0.25em] text-aqua">Features</p>
                        <h2 className="mt-4 max-w-lg text-3xl font-light leading-tight tracking-tight sm:text-4xl lg:text-5xl">
                            Fewer clicks. More music.
                        </h2>
                    </div>
                    <p className="max-w-sm text-sm leading-relaxed text-zinc-400">
                        ENGINE stays out of the way until you speak — then it works the session so
                        you can stay in it.
                    </p>
                </div>
            </Reveal>

            <div
                data-testid={HOME.featuresGrid}
                className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-12"
            >
                {/* Voice control — wide, with treated studio photography */}
                <Reveal className="md:col-span-7" delay={0.05}>
                    <CardShell className="min-h-[420px]">
                        <img
                            src={MIC_DETAIL}
                            alt="Studio microphone under a soft spotlight"
                            className="absolute inset-0 h-full w-full object-cover opacity-60 transition-transform duration-700 group-hover:scale-[1.03]"
                        />
                        <div
                            aria-hidden="true"
                            className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-ink/5"
                        />
                        <div className="relative flex h-full flex-col p-8 md:p-10">
                            <IconChip icon={Mic} />
                            <h3 className="mt-6 text-2xl font-light tracking-tight text-white">
                                Voice control for FL Studio
                            </h3>
                            <p className="mt-3 max-w-md text-sm leading-relaxed text-zinc-400">
                                Speak naturally while the session keeps rolling. ENGINE listens in
                                the background, so reaching for a fader never means breaking your
                                flow.
                            </p>
                        </div>
                    </CardShell>
                </Reveal>

                {/* Offline recognition */}
                <Reveal className="md:col-span-5" delay={0.12}>
                    <CardShell className="flex h-full min-h-[420px] flex-col justify-between p-8 md:p-10">
                        <div>
                            <IconChip icon={WifiOff} />
                            <h3 className="mt-6 text-2xl font-light tracking-tight text-white">
                                Offline voice recognition
                            </h3>
                            <p className="mt-3 text-sm leading-relaxed text-zinc-400">
                                Recognition runs offline. No connection required, no audio leaving
                                the room — the session never waits on the network.
                            </p>
                        </div>
                        <div className="mt-8 flex items-center justify-between">
                            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-aqua">
                                Works offline
                            </span>
                            <AudioWaveform className="h-8 w-8 text-aqua/40" aria-hidden="true" />
                        </div>
                    </CardShell>
                </Reveal>

                {/* Wake phrases */}
                <Reveal className="md:col-span-4" delay={0.05}>
                    <CardShell className="flex h-full min-h-[300px] flex-col justify-between p-8">
                        <div>
                            <h3 className="text-xl font-light tracking-tight text-white">Wake phrases</h3>
                            <p className="mt-3 text-sm leading-relaxed text-zinc-400">
                                Two phrases bring ENGINE to attention, even mid-session.
                            </p>
                        </div>
                        <div className="mt-8 flex flex-wrap gap-2">
                            {['“Engineer”', '“Engine”'].map((phrase) => (
                                <span
                                    key={phrase}
                                    className="rounded-full border border-aqua/30 bg-aqua/5 px-4 py-1.5 font-mono text-sm text-aqua"
                                >
                                    {phrase}
                                </span>
                            ))}
                        </div>
                    </CardShell>
                </Reveal>

                {/* Optional chime */}
                <Reveal className="md:col-span-4" delay={0.12}>
                    <CardShell className="flex h-full min-h-[300px] flex-col justify-between p-8">
                        <div>
                            <h3 className="text-xl font-light tracking-tight text-white">Optional chime</h3>
                            <p className="mt-3 text-sm leading-relaxed text-zinc-400">
                                A soft chime confirms the wake word was recognized. Prefer silence?
                                Turn it off anytime.
                            </p>
                        </div>
                        <div className="mt-8 flex items-center gap-4">
                            <span className="relative flex h-8 w-8 items-center justify-center">
                                <span className="chime-ripple absolute h-6 w-6 rounded-full border border-aqua/40" />
                                <span
                                    className="chime-ripple absolute h-6 w-6 rounded-full border border-aqua/30"
                                    style={{ animationDelay: '1.1s' }}
                                />
                                <BellRing className="h-4 w-4 text-aqua" />
                            </span>
                            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                                On / off — your choice
                            </span>
                        </div>
                    </CardShell>
                </Reveal>

                {/* Mic-level display & voice test */}
                <Reveal className="md:col-span-4" delay={0.19}>
                    <CardShell className="flex h-full min-h-[300px] flex-col justify-between p-8">
                        <div>
                            <h3 className="text-xl font-light tracking-tight text-white">
                                Mic level &amp; voice test
                            </h3>
                            <p className="mt-3 text-sm leading-relaxed text-zinc-400">
                                A live microphone-level display and a built-in voice-test mode, so
                                you know your input is clean before the take.
                            </p>
                        </div>
                        <div className="mt-8 flex items-center justify-between gap-4">
                            <MiniEq bars={7} className="h-6" />
                            <button
                                data-testid={HOME.voiceTestLink}
                                onClick={scrollToId('voice-test')}
                                className="text-sm text-aqua underline-offset-4 transition-colors hover:text-white hover:underline"
                            >
                                Try the voice test
                            </button>
                        </div>
                    </CardShell>
                </Reveal>
            </div>
        </div>
    </section>
);
